import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { Button } from '@shared/ui/button';
import { SuiteIcon } from '@shared/icons';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';
import { useActiveSuite } from '../useActiveSuite';
import { cn } from '@shared/lib/utils';

export const AppSwitcher: React.FC = () => {
  const { hasPermission } = useAuth();
  const activeSuite = useActiveSuite();

  const permittedSuites = allSuites
    .filter((s) => s.id !== 'inventory' && s.id !== 'crm')
    .filter((suite) =>
      suite.requiredPermission ? hasPermission(suite.requiredPermission) : true
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-navbar-muted hover:text-navbar-foreground hover:bg-navbar-accent shrink-0"
          aria-label="Apps dropdown"
          title="Switch Suite / Apps"
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[320px] p-2" sideOffset={8}>
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
          Suites & Applications
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1" />
        <div className="grid grid-cols-3 gap-1.5 p-1">
          {permittedSuites.map((suite) => {
            const isActive = activeSuite?.id === suite.id;
            return (
              <DropdownMenuItem asChild key={suite.id} className="p-0 focus:bg-transparent">
                <Link
                  to={suite.basePath}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1.5 rounded-lg p-2.5 text-center transition-colors hover:bg-accent focus:bg-accent',
                    isActive && 'bg-accent/80 font-medium ring-1 ring-primary'
                  )}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <SuiteIcon name={suite.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] leading-tight line-clamp-2 text-foreground">
                    {suite.name}
                  </span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
